/**
 * Typewriter Animation Variant (Web)
 *
 * Simple text reveal with cursor effect. Default variant for A/B testing.
 * Includes 5-phase system: EMERGENCE, TRANSITION, READING, CONVERGENCE, REVELATION.
 */

import { useEffect, useState, useMemo, useCallback, useRef, memo } from 'react';
import { View, Text, Pressable, Animated } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from '@dramadetector/shared';
import type { LetterThrowingAnimationProps } from './types';
import { PHASE_TIMING } from './types';
import { getCategoryFromScore, getCategoryStyle } from '../../lib/categoryStyles';
import { getPhaseFromProgress, animateBadgeReveal, REVELATION_START, ANIMATION_BG_GRADIENT } from './animationHelpers.web';
import { styles } from './animationStyles.web';
import DramaBadgeView from './DramaBadgeView.web';

const TypewriterAnimation = memo(function TypewriterAnimation({
  text,
  speed = '3',
  onComplete,
  onSkip,
  style,
  analysisResult,
}: LetterThrowingAnimationProps) {
  const { t } = useTranslation('home');
  const [showSkipHint, setShowSkipHint] = useState(false);
  const [progress, setProgress] = useState(0);
  const [badgeScale] = useState(new Animated.Value(0));
  const [badgeOpacity] = useState(new Animated.Value(0));
  const [textOpacity] = useState(new Animated.Value(1));
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const badgeRevealedRef = useRef(false);

  const phase = getPhaseFromProgress(progress);
  const category = analysisResult?.category || getCategoryFromScore(analysisResult?.score || 50);
  const categoryStyle = getCategoryStyle(category);

  const emergenceProgress = useMemo(() => {
    if (progress <= PHASE_TIMING.emergence) {
      return progress / PHASE_TIMING.emergence;
    }
    return 1;
  }, [progress]);

  const visibleLength = Math.floor(text.length * emergenceProgress);
  const visibleText = text.slice(0, visibleLength);

  useEffect(() => {
    const duration = speed === '1' ? 12000 : speed === '1.5' ? 8000 : speed === '2' ? 5000 : 3500;
    const interval = 50;
    const steps = duration / interval;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const newProgress = Math.min(step / steps, 1);
      setProgress(newProgress);

      if (step >= steps) {
        clearInterval(timer);
        intervalRef.current = null;
        completionTimerRef.current = setTimeout(() => onComplete?.(), 600);
      }
    }, interval);
    intervalRef.current = timer;

    const hintTimer = setTimeout(() => setShowSkipHint(true), 1000);

    return () => {
      clearInterval(timer);
      intervalRef.current = null;
      clearTimeout(hintTimer);
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
    };
  }, [speed, onComplete]);

  useEffect(() => {
    if (badgeRevealedRef.current) return;
    if (progress >= REVELATION_START && analysisResult) {
      badgeRevealedRef.current = true;
      Animated.timing(textOpacity, {
        toValue: 0.3,
        duration: 300,
        useNativeDriver: true,
      }).start();

      animateBadgeReveal(badgeScale, badgeOpacity).start();
    }
  }, [progress, analysisResult, badgeScale, badgeOpacity, textOpacity]);

  const handleSkip = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    onSkip?.();
  }, [onSkip]);

  const phaseLabel = useMemo(() => {
    switch (phase) {
      case 'emergence':
      case 'transition':
      case 'reading':
        return t('home.modern.processing');
      case 'convergence':
        return analysisResult ? '' : t('home.modern.processing');
      default:
        return '';
    }
  }, [phase, analysisResult, t]);

  return (
    <Pressable
      onPress={handleSkip}
      style={[styles.container, style]}
      accessibilityRole="button"
      accessibilityLabel={t('home.modern.skip_animation')}
    >
      <LinearGradient
        colors={ANIMATION_BG_GRADIENT}
        style={styles.gradient}
      >
        <View style={styles.content}>
          <Animated.View style={[styles.textContainer, { opacity: textOpacity }]}>
            <Text style={styles.text}>
              {visibleText}
              {phase === 'emergence' && <Text style={styles.cursor}>|</Text>}
            </Text>
            <Text style={[styles.text, styles.chromaticPink, { position: 'absolute' }]}>
              {visibleText}
            </Text>
            <Text style={[styles.text, styles.chromaticMint, { position: 'absolute' }]}>
              {visibleText}
            </Text>
          </Animated.View>

          <DramaBadgeView
            analysisResult={analysisResult}
            badgeOpacity={badgeOpacity}
            badgeScale={badgeScale}
            categoryStyle={categoryStyle}
          />

          <View style={styles.progressContainer}>
            <View style={[styles.progressBar, { width: `${progress * 100}%` }]} />
          </View>

          {phaseLabel && <Text style={styles.phaseLabel}>{phaseLabel}</Text>}
          {showSkipHint && phase !== 'complete' && (
            <Text style={styles.skipHint}>{t('home.modern.tap_to_skip')}</Text>
          )}
        </View>
      </LinearGradient>
    </Pressable>
  );
});

export default TypewriterAnimation;
